import type { NormalizedOrders } from "../../modules/orders-reader/normalization";
import {
  splitIntoChunks,
  type OrdersParseTree,
} from "../../modules/orders-reader/parser";
import type { ItemNode } from "../../modules/orders-reader/parser/types";
import type { RateZone } from "./calibration";
import type { SpeechSegment, SpeechSegmentKind } from "./schedule";

interface StreamNode {
  readonly id: string;
  readonly kind: Exclude<SpeechSegmentKind, "chunk">;
}

function streamNodes(tree: OrdersParseTree): readonly StreamNode[] {
  const result: StreamNode[] = [];
  const addItem = (item: ItemNode): void => {
    result.push({ id: item.id, kind: "item" });
  };
  for (const paragraph of tree.paragraphs) {
    result.push({ id: paragraph.id, kind: "paragraph-heading" });
    paragraph.items.forEach(addItem);
    for (const subparagraph of paragraph.subparagraphs) {
      result.push({ id: subparagraph.id, kind: "subparagraph-heading" });
      subparagraph.items.forEach(addItem);
    }
  }
  return result;
}

/**
 * Whether a node's first chunk is its heading rather than its first saying.
 *
 * **Ruling 86**, 17 October 2026 (b) section 3:
 *
 * > "'(x2)' repeats the content, not its heading. The heading is said once,
 * > before the first saying; the content is then said twice. This holds
 * > wherever the reader repeats: the probable mission in a warning order and
 * > the mission in orders."
 *
 * The chunker splits a clause at a colon, so an item written as a bold label
 * and a value hands back the label as a chunk of its own. That chunk is the
 * heading. This is measured on the chunks and not read off the parse, because
 * the parse types the warning order's probable mission `numbered`: its bold
 * run sits inside a numbered item, where no label is recorded. A first chunk
 * that closes a sentence is not a heading; it is the first thing said.
 */
function firstChunkIsHeading(chunks: readonly string[]): boolean {
  const [first] = chunks;
  if (first === undefined || chunks.length < 2) return false;
  return first.trimEnd().endsWith(":") && !/[.!?]/.test(first);
}

function nodeZone(
  nodeId: string,
  criticalByNode: ReadonlyMap<string, readonly string[]>,
): RateZone {
  const kinds = criticalByNode.get(nodeId) ?? [];
  if (kinds.includes("mission")) return "mission";
  return kinds.length > 0 ? "critical" : "body";
}

export function ordersSpeechSegments(
  normalized: NormalizedOrders,
  maximumChunkLength = 200,
): readonly SpeechSegment[] {
  const nodes = streamNodes(normalized.displayTree);
  if (nodes.length !== normalized.spokenTextStream.length) {
    throw new Error(
      "The normalized speech stream does not match the parsed orders tree.",
    );
  }
  const criticalByNode = new Map<string, string[]>();
  for (const span of normalized.criticalSpans) {
    const kinds = criticalByNode.get(span.nodeId) ?? [];
    kinds.push(span.kind);
    criticalByNode.set(span.nodeId, kinds);
  }

  const repeated = new Set(normalized.repeatedNodeIds);

  return nodes.flatMap((node, streamIndex) => {
    const text = normalized.spokenTextStream[streamIndex];
    /* v8 ignore next -- length equality above guarantees this invariant */
    if (text === undefined) return [];
    const chunks = splitIntoChunks(text, maximumChunkLength);
    const nodeIsHeading = node.kind !== "item";
    const zone = nodeIsHeading ? "heading" : nodeZone(node.id, criticalByNode);
    const segments = chunks.map(
      (chunk, chunkIndex): SpeechSegment => ({
        id: `${node.id}/speech-${String(chunkIndex + 1).padStart(2, "0")}`,
        kind: chunkIndex === 0 ? node.kind : "chunk",
        text: chunk,
        zone,
      }),
    );
    // Ruling 82, his item 18:
    // "dont say (x2), say the probable mission twice". The marker is already
    // out of the text; this is the saying twice.
    //
    // A mission-zone node is left alone, because the schedule repeats the
    // whole mission span already and a marker inside it would say the
    // mission four times.
    if (!repeated.has(node.id) || zone === "mission") return segments;
    // Ruling 86: the heading is said once, before the first saying, and the
    // content is then said twice. He heard "Probable Mission" twice because
    // every chunk of the node was repeated, and the label is a chunk.
    const said = firstChunkIsHeading(chunks) ? segments.slice(1) : segments;
    return [
      ...segments,
      ...said.map((segment) => ({
        ...segment,
        id: `${segment.id}/repeat-02`,
      })),
    ];
  });
}
